import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Masthead } from "@/components/Masthead";
import { Footer } from "@/components/Footer";
import { ArticleModal } from "@/components/ArticleModal";
import { StoryCard } from "@/components/StoryCard";
import { SectionLabel } from "@/components/SectionLabel";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bookmark, ArrowLeft, Share2, Mail, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SavedPage = () => {
  const { user, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [savedArticles, setSavedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [digestSubscribed, setDigestSubscribed] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      fetchSavedArticles();
      fetchDigestStatus();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [user, authLoading]);

  const fetchSavedArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookmarks`, { withCredentials: true });
      setSavedArticles(response.data);
      setBookmarkedIds(new Set(response.data.map(a => a.id)));
    } catch (error) {
      console.error("Error fetching saved articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDigestStatus = async () => {
    try {
      const response = await axios.get(`${API}/digest/status`, { withCredentials: true });
      setDigestSubscribed(response.data.subscribed);
    } catch (error) {
      console.error("Error fetching digest status:", error);
    }
  };

  const handleDigestToggle = async (checked) => {
    try {
      await axios.post(`${API}/digest/subscribe`, { subscribe: checked }, { withCredentials: true });
      setDigestSubscribed(checked);
      toast.success(checked ? "Subscribed to daily digest!" : "Unsubscribed from daily digest");
    } catch (error) {
      console.error("Error updating digest:", error);
      toast.error("Failed to update subscription");
    }
  };

  const handleShareList = async () => {
    if (savedArticles.length === 0) {
      toast.error("No articles to share");
      return;
    }

    try {
      // /shared/{id} is a public, unauthenticated, non-expiring page. Keep names
      // out of the title — the mobile app does the same, and the privacy notice
      // says so.
      const response = await axios.post(`${API}/share-list`, {
        title: "Our Reading List"
      }, { withCredentials: true });
      
      const fullUrl = `${window.location.origin}/shared/${response.data.id}`;
      setShareUrl(fullUrl);
      toast.success("Reading list shared!");
    } catch (error) {
      console.error("Error sharing list:", error);
      toast.error("Failed to share list");
    }
  };

  const copyShareUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleBookmark = async (articleId) => {
    try {
      await axios.delete(`${API}/bookmarks/${articleId}`, { withCredentials: true });
      setSavedArticles(prev => prev.filter(a => a.id !== articleId));
      setBookmarkedIds(prev => {
        const next = new Set(prev);
        next.delete(articleId);
        return next;
      });
      toast.success("Removed from saved");
    } catch (error) {
      console.error("Remove bookmark error:", error);
    }
  };

  const openArticle = (article) => {
    setSelectedArticle(article);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedArticle(null);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-scoop-cream flex items-center justify-center">
        <div className="text-center">
          <div className="font-display text-3xl font-bold text-scoop-ink mb-2">
            The <em className="text-scoop-blue">Scoop</em>
          </div>
          <p className="text-scoop-muted text-sm">Loading saved stories...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="saved-page">
        <Masthead activeCategory="saved" onCategoryChange={() => navigate("/")} />
        
        <div className="max-w-[600px] mx-auto px-5 py-16 text-center">
          <div className="bg-white rounded-lg border border-scoop-border p-8">
            <Bookmark className="w-16 h-16 text-scoop-blue mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-scoop-ink mb-3">
              Save Stories for Later
            </h2>
            <p className="text-scoop-muted mb-6">
              Parents can sign in to save stories and create a reading list for their kids.
            </p>
            <Button 
              onClick={login}
              className="bg-scoop-blue hover:bg-scoop-blue/90 text-white px-8 py-3 rounded-full font-bold"
              data-testid="login-btn"
            >
              Sign in with Google
            </Button>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="scoop-page min-h-screen bg-scoop-cream" data-testid="saved-page">
      <Masthead activeCategory="saved" onCategoryChange={() => navigate("/")} />
      
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-8">
        <button 
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-scoop-muted hover:text-scoop-blue mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <SectionLabel icon="📚" title={`${user.name}'s Saved Stories`} />
          
          <div className="flex flex-wrap items-center gap-4">
            {/* Daily Digest Toggle */}
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-scoop-border">
              <Mail className="w-4 h-4 text-scoop-muted" />
              <span className="text-sm font-medium text-scoop-ink">Daily Digest</span>
              <Switch
                checked={digestSubscribed}
                onCheckedChange={handleDigestToggle}
                data-testid="digest-toggle"
              />
            </div>
            
            {/* Share Button */}
            <Button
              onClick={handleShareList}
              variant="outline"
              className="border-scoop-blue text-scoop-blue hover:bg-scoop-blue hover:text-white"
              disabled={savedArticles.length === 0}
              data-testid="share-list-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share List
            </Button>
          </div>
        </div>
        
        {/* Share URL Display */}
        {shareUrl && (
          <div className="bg-[#EEF5FF] border border-[#C8DEFF] rounded-lg p-4 mb-6 flex items-center gap-3">
            <ExternalLink className="w-5 h-5 text-scoop-blue flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-scoop-ink mb-1">Your reading list is ready to share!</p>
              <p className="text-xs text-scoop-muted truncate">{shareUrl}</p>
            </div>
            <Button
              onClick={copyShareUrl}
              variant="ghost"
              size="sm"
              className="flex-shrink-0"
            >
              {copied ? <Check className="w-4 h-4 text-scoop-sage" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        )}
        
        {/* Digest Info */}
        {digestSubscribed && (
          <div className="bg-[#F0FBF5] border border-[#A7E5C5] rounded-lg p-4 mb-6">
            <p className="text-sm text-[#1A5C3A]">
              <strong>Daily Digest Active:</strong> You'll receive a summary of new stories each morning. 
              <span className="text-xs ml-1">(Email sending is currently in test mode)</span>
            </p>
          </div>
        )}
        
        {savedArticles.length === 0 ? (
          <div className="text-center py-12">
            <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-scoop-muted">No saved stories yet.</p>
            <p className="text-sm text-gray-400 mt-2">
              Click the bookmark icon on any story to save it here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedArticles.map((article, index) => (
              <StoryCard 
                key={article.id} 
                article={article} 
                onClick={() => openArticle(article)}
                delay={index + 1}
                isBookmarked={true}
                onBookmark={() => handleBookmark(article.id)}
              />
            ))}
          </div>
        )}
      </div>
      
      <Footer />
      
      <ArticleModal 
        article={selectedArticle} 
        isOpen={modalOpen} 
        onClose={closeModal}
        isBookmarked={selectedArticle ? bookmarkedIds.has(selectedArticle.id) : false}
        onBookmark={selectedArticle ? () => handleBookmark(selectedArticle.id) : null}
      />
    </div>
  );
};

export default SavedPage;
